import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { backend, openVirtualScreen, useApp } from '../data/store';
import { Icon, type IconName } from '../ui/icons';
import { openSheet, useNav } from '../app/ui';

interface Item { id: string; icon: IconName; title: string; sub: string; run: () => void }

/** ⌘K: jump to any screen, page or action. */
export function SearchSheet({ onClose }: { onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  const nav = useNav();
  const [q, setQ] = useState('');
  const [i, setI] = useState(0);
  const go = (path: string) => () => { onClose(); nav(path); };
  const act = (fn: () => void) => () => { onClose(); setTimeout(fn, 0); };
  const all = useMemo<Item[]>(() => [
    ...ws.screens.map((s) => ({ id: `s${s.id}`, icon: 'monitor' as const, title: s.name, sub: 'Edit in Studio', run: go(`/studio/${s.id}`) })),
    { id: 'p-dash', icon: 'grid', title: 'Dashboard', sub: 'Page', run: go('/') },
    { id: 'p-screens', icon: 'tv', title: 'All screens', sub: 'Page', run: go('/screens') },
    { id: 'p-tt', icon: 'pencil', title: 'Edit timetable', sub: 'Full year view', run: go('/timetable') },
    { id: 'p-set', icon: 'gear', title: 'Settings', sub: 'Masjid, prayer times, team', run: go('/settings') },
    { id: 'a-sch', icon: 'schedule', title: 'Schedule content', sub: 'Push to a screen', run: act(() => openSheet({ name: 'schedule' })) },
    { id: 'a-csv', icon: 'upload', title: 'Upload timetable CSV', sub: 'Prayer timetable', run: act(() => openSheet({ name: 'csv' })) },
    { id: 'a-jum', icon: 'calendar', title: 'Jumu‘ah times', sub: 'Khutbah & jama‘ah', run: act(() => openSheet({ name: 'jumuah' })) },
    { id: 'a-eid', icon: 'moon', title: 'Eid times', sub: 'Salah & takbeer', run: act(() => openSheet({ name: 'eid' })) },
    { id: 'a-add', icon: 'plus', title: 'Add screen', sub: 'Pair a new display', run: act(() => openSheet({ name: 'addScreen' })) },
    ...(backend.kind === 'sandbox' ? [{ id: 'a-virt', icon: 'tv' as const, title: 'Open a virtual screen', sub: 'Simulated display', run: act(() => openVirtualScreen()) }] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [ws.screens]);
  const list = all.filter((x) => `${x.title} ${x.sub}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 9);
  const sel = Math.min(i, Math.max(0, list.length - 1));

  return createPortal(
    <div className="scrim" style={{ placeItems: 'start center', paddingTop: '12vh' }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sheet palette" role="dialog" aria-modal="true" aria-label="Search">
        <label className="pal-input"><Icon name="search" size={22} />
          <input autoFocus className="bare" placeholder="Search screens, pages and actions" value={q} aria-label="Search" role="combobox" aria-expanded aria-controls="pal-list" aria-activedescendant={list[sel]?.id}
            onChange={(e) => { setQ(e.target.value); setI(0); }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setI((sel + 1) % Math.max(1, list.length)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setI((sel - 1 + list.length) % Math.max(1, list.length)); }
              else if (e.key === 'Enter') { e.preventDefault(); list[sel]?.run(); }
              else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
            }} />
          <kbd>esc</kbd>
        </label>
        <ul id="pal-list" role="listbox" className="pal-list">
          {list.map((x, k) => (
            <li key={x.id} id={x.id} role="option" aria-selected={k === sel} className={k === sel ? 'on' : ''} onMouseEnter={() => setI(k)} onClick={x.run}>
              <span className="ic"><Icon name={x.icon} size={20} /></span><span><b>{x.title}</b><small>{x.sub}</small></span>
            </li>
          ))}
          {!list.length && <li className="hint" style={{ padding: 16 }}>Nothing matches “{q}”.</li>}
        </ul>
      </div>
    </div>, document.body);
}
